import {
  InstantSearch,
  SearchBox,
  Hits,
  Configure,
  Stats,
  Pagination,
  RefinementList,
  ClearRefinements,
  useInstantSearch,
} from 'react-instantsearch';
import { searchClient, INDEX_NAME, ALGOLIA_CONFIG } from '@/lib/algolia';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from '@/auth/AuthProvider';
import { useRouteTitle } from "../hooks/useRouteTitle";
import type { JournalEntryHit } from '@/lib/algolia';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { useEffect, useRef } from 'react';

/**
 * Search Query Logger component
 * Logs search queries to Firestore when search is executed
 * Work Order 3.3 (128): Search Query Analytics Logging
 */
function SearchQueryLogger({ userId }: { userId: string }) {
  const { results } = useInstantSearch();
  const lastLoggedQuery = useRef<string>('');
  
  useEffect(() => {
    // Only log when we have results and a query
    if (!results || !results.query) {
      return;
    }
    
    // Avoid logging the same query multiple times
    if (lastLoggedQuery.current === results.query) {
      return;
    }
    
    lastLoggedQuery.current = results.query;
    
    // Log the query to Firestore
    const logQuery = async () => {
      try {
        const logSearchQuery = httpsCallable(functions, 'logSearchQuery');
        
        await logSearchQuery({
          userId,
          query: results.query,
          filters: results.getRefinements ? results.getRefinements() : {},
          resultsCount: results.nbHits,
        });
      } catch (error) {
        // Silent failure - don't disrupt user experience
        console.error('[SearchQueryLogger] Failed to log search query:', error);
      }
    };
    
    logQuery();
  }, [results, userId]);
  
  return null;
}

/**
 * No Results Suggestions component
 * Shows static suggestions when search returns zero results
 */
function NoResultsSuggestions() {
  const { results } = useInstantSearch();
  
  // Only show when query exists and no results found
  if (!results || results.nbHits > 0 || !results.query) {
    return null;
  }
  
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-6 text-center">
      <h3 className="text-lg font-semibold text-slate-50 mb-3">No results found</h3>
      <div className="space-y-2 text-sm text-slate-300">
        <p>Try adjusting your filters.</p>
        <p>Try searching journal entries or filings.</p>
      </div>
    </div>
  );
}

/**
 * Search result hit component
 */
function Hit({ hit }: { hit: JournalEntryHit }) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (hit.caseId && hit.objectID) {
      navigate(`/cases/${hit.caseId}/journal/${hit.objectID}`);
    } else {
      toast.error('Unable to navigate to entry');
    }
  };

  return (
    <article
      onClick={handleClick}
      className="cursor-pointer rounded-lg border border-slate-700 bg-slate-900/50 p-4 transition hover:shadow-md hover:border-brand-orange"
    >
      <header className="mb-2">
        <h3 
          className="font-semibold text-white"
          dangerouslySetInnerHTML={{ 
            __html: hit._highlightResult?.title?.value || hit.title 
          }}
        />
        {hit.occurredAt && (
          <time className="text-xs text-slate-400">
            {formatDistanceToNow(new Date(hit.occurredAt), { addSuffix: true })}
          </time>
        )}
      </header>
      
      {hit._snippetResult?.narrative?.value && (
        <p 
          className="text-sm text-slate-300 line-clamp-2"
          dangerouslySetInnerHTML={{ 
            __html: hit._snippetResult.narrative.value 
          }}
        />
      )}
      
      {hit.aiTags && hit.aiTags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {hit.aiTags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="inline-flex items-center rounded-full bg-brand-orange/10 px-2 py-0.5 text-xs font-medium text-brand-orange"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

export default function SearchPage() {
  useRouteTitle("Search");
  const { user } = useAuthState();

  if (!user) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        <p className="text-gray-600">Please sign in to search.</p>
      </main>
    );
  }

  // Check if Algolia is configured
  const isAlgoliaConfigured = 
    import.meta.env.VITE_ALGOLIA_APP_ID && 
    import.meta.env.VITE_ALGOLIA_SEARCH_ONLY_API_KEY;

  if (!isAlgoliaConfigured) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <h2 className="font-semibold text-slate-50">Search not configured</h2>
          <p className="mt-1 text-sm text-gray-600">
            Algolia environment variables are not set. Please configure VITE_ALGOLIA_APP_ID and VITE_ALGOLIA_SEARCH_ONLY_API_KEY.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-6">
      <InstantSearch
        searchClient={searchClient}
        indexName={INDEX_NAME}
      >
        {/* User-scoped filtering - only show results for this user */}
        <Configure
          {...ALGOLIA_CONFIG}
          filters={`userId:${user.uid}`}
        />
        
        {/* Search Query Logger - logs queries for admin analytics */}
        <SearchQueryLogger userId={user.uid} />

        <header className="mb-6">
          <h1 className="text-2xl font-semibold text-white">Search Workspace</h1>
          <p className="mt-1 text-sm text-slate-300">
            Search across all your journal entries
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Sidebar with filters */}
          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Filters</h3>
                <ClearRefinements
                  translations={{
                    resetButtonText: 'Clear all filters',
                  }}
                  classNames={{
                    button: 'text-xs text-brand-orange hover:text-brand-orange/80 font-medium',
                  }}
                />
              </div>

              {/* Tags filter */}
              <div>
                <h4 className="text-xs font-medium text-slate-200 mb-2">Tags</h4>
                <RefinementList
                  attribute="aiTags"
                  limit={5}
                  showMore={true}
                  classNames={{
                    list: 'space-y-1',
                    item: 'flex items-center',
                    label: 'flex items-center text-sm text-slate-300 cursor-pointer',
                    checkbox: 'mr-2 rounded border-slate-600',
                    count: 'ml-auto text-xs text-slate-400',
                  }}
                />
              </div>
            </div>
          </aside>

          {/* Main content area */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search box */}
            <SearchBox
              placeholder="Search journal entries..."
              classNames={{
                root: 'w-full',
                form: 'relative',
                input: 'w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-3 pr-10 text-sm text-white placeholder-slate-400 focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20',
                submit: 'absolute right-3 top-3',
                reset: 'absolute right-10 top-3',
              }}
            />

            {/* Results stats */}
            <div className="flex items-center justify-between">
              <Stats
                classNames={{
                  root: 'text-sm text-gray-600',
                }}
              />
            </div>

            {/* No Results Suggestions */}
            <NoResultsSuggestions />
            
            {/* Results */}
            <Hits
              hitComponent={Hit}
              classNames={{
                root: 'space-y-3',
                list: 'space-y-3',
                item: 'list-none',
              }}
            />

            {/* Pagination */}
            <div className="mt-6 flex justify-center">
              <Pagination
                classNames={{
                  root: 'flex items-center gap-2',
                  list: 'flex items-center gap-1',
                  item: 'inline-block',
                  link: 'px-3 py-1 rounded border border-gray-300 text-sm text-gray-700 hover:bg-slate-700',
                selectedItem: 'bg-brand-orange border-brand-orange text-white',
                  disabledItem: 'opacity-50 cursor-not-allowed',
                }}
              />
            </div>
          </div>
        </div>
      </InstantSearch>
    </main>
  );
}
